import { Mail, Phone, ArrowUpRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import CTA from "@/components/CTA";
import { QUALIFICATIONS, MILESTONES, CONTACT } from "@/data/content";

function DirectorHero() {
  return (
    <section className="max-w-7xl mx-auto px-6 py-24 sm:py-32 grid lg:grid-cols-2 gap-16 items-center">
      <Reveal>
        <div className="relative group max-w-md">
          <div className="absolute -inset-3 border border-zinc-800 pointer-events-none" />
          <span className="absolute -top-3 -left-3 w-6 h-6 border-t-2 border-l-2 border-[#C5A059] z-10" />
          <span className="absolute -bottom-3 -right-3 w-6 h-6 border-b-2 border-r-2 border-[#C5A059] z-10" />
          <div className="relative overflow-hidden aspect-[4/5] bg-void-2">
            <img
              src="/assets/carlo-headshot.jpeg"
              alt="Carlo D'Emidio, Founder & Director of Fox Project Management"
              data-testid="director-headshot"
              className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-void/60 via-transparent to-transparent" />
          </div>
          <div className="absolute bottom-5 left-5 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300">
            Founder — Est. Edinburgh
          </div>
        </div>
      </Reveal>
      <div>
        <Reveal>
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-3">
            <span className="w-10 h-px bg-[#C5A059]" /> Founder &amp; Director
          </p>
          <h2 data-testid="director-name" className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-zinc-50 leading-tight">
            Carlo D'Emidio
          </h2>
          <p data-testid="director-title" className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-[#C5A059]">
            Director — Fox Project Management
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-8 text-base text-zinc-300 font-light leading-relaxed">
            Carlo D'Emidio is a Chartered construction and development professional with over two decades of
            experience delivering complex infrastructure, residential, mixed-use and commercial projects. He is the
            founder of Fox Project Management, bringing his expertise in leadership, governance and stakeholder
            management to clients across the sector.
          </p>
          <p className="mt-5 text-base text-zinc-300 font-light leading-relaxed">
            His career spans director and management roles with consultancies, private developers and public bodies.
            With strong commercial acumen and a focus on delivering value for money, Carlo has extensive expertise in
            design management and a proven track record of securing planning consents, technical approvals and
            managing procurement — leading developments valued at up to £300 million.
          </p>
        </Reveal>
        <Reveal delay={0.25}>
          <div className="mt-8 flex flex-wrap gap-3">
            {QUALIFICATIONS.map((q) => (
              <span
                key={q.abbr}
                data-testid={q.testid}
                className="border border-zinc-700 px-4 py-2 font-mono text-[11px] tracking-[0.15em] text-zinc-300 hover:border-[#C5A059] hover:text-zinc-50 transition-colors duration-300"
                title={`${q.name} (${q.year})`}
              >
                {q.abbr}
              </span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.35}>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href={`mailto:${CONTACT.emailCarlo}`}
              data-testid="director-email-link"
              className="group inline-flex items-center gap-3 bg-zinc-50 text-zinc-950 px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#C5A059] transition-colors duration-300"
            >
              <Mail className="w-4 h-4" /> {CONTACT.emailCarlo}
            </a>
            <a
              href={CONTACT.phoneHref}
              data-testid="director-phone-link"
              className="inline-flex items-center gap-3 border border-zinc-700 text-zinc-200 px-6 py-3.5 font-mono text-xs uppercase tracking-[0.15em] hover:border-zinc-400 transition-colors duration-300"
            >
              <Phone className="w-4 h-4" /> {CONTACT.phone}
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Milestones() {
  return (
    <section className="border-y border-zinc-800/60 bg-void-2/30">
      <div className="max-w-7xl mx-auto px-6 py-24 sm:py-28">
        <Reveal>
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-500 flex items-center gap-3">
            <span className="w-10 h-px bg-[#C5A059]" /> Career milestones
          </p>
        </Reveal>
        <div className="mt-12 grid md:grid-cols-2 gap-px bg-zinc-800/60 border border-zinc-800/60">
          {MILESTONES.map((m, i) => (
            <Reveal key={m.title} delay={i * 0.1}>
              <div className="h-full bg-void p-8 sm:p-10 hover:bg-void-2 transition-colors duration-500">
                <span className="font-display text-3xl font-extrabold text-zinc-800">0{i + 1}</span>
                <h3 className="mt-4 font-display text-lg sm:text-xl font-bold text-zinc-100">{m.title}</h3>
                <p className="mt-3 text-sm text-zinc-400 font-light leading-relaxed">{m.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-16 border-l-2 border-[#C5A059] pl-8 py-2 max-w-3xl">
            <p className="font-display text-xl sm:text-2xl font-bold text-zinc-100 leading-snug">
              "A genuine commitment to quality and being different from the rest — a personal, hands-on approach to
              every project we deliver."
            </p>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              Carlo D'Emidio — Director
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Commitment() {
  return (
    <section className="max-w-7xl mx-auto px-6 py-24 sm:py-28 grid lg:grid-cols-2 gap-12 items-center">
      <Reveal>
        <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-zinc-100 leading-tight">
          Director-led, on every instruction
        </h2>
        <p className="mt-6 text-base text-zinc-300 font-light leading-relaxed">
          When you appoint Fox, you deal directly with the director from first conversation to handover. No hand-offs,
          no layers — just clear, practical project management expertise with hands-on involvement at every stage.
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="border border-zinc-800/80 bg-void-2/30 p-8 sm:p-10">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Direct line to the director</p>
          <div className="mt-6 space-y-4 text-sm">
            <a href={`mailto:${CONTACT.emailCarlo}`} className="flex items-center gap-3 text-zinc-300 hover:text-zinc-50 transition-colors">
              <Mail className="w-4 h-4 text-[#C5A059]" /> {CONTACT.emailCarlo}
            </a>
            <a href={CONTACT.phoneHref} className="flex items-center gap-3 text-zinc-300 hover:text-zinc-50 transition-colors">
              <Phone className="w-4 h-4 text-[#C5A059]" /> {CONTACT.phone}
            </a>
          </div>
          <a
            href={`mailto:${CONTACT.emailCarlo}`}
            data-testid="team-contact-card-button"
            className="group mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-200 hover:text-[#C5A059] transition-colors"
          >
            Arrange an introduction <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </a>
        </div>
      </Reveal>
    </section>
  );
}

export default function Team() {
  return (
    <>
      <PageHeader
        overline="Meet the team"
        lines={["The person behind", "the name"]}
        intro="Fox Project Management is intentionally personal: one chartered director, 22+ years of delivery experience, and direct accountability on every instruction."
        testid="team-headline"
      />
      <DirectorHero />
      <Milestones />
      <Commitment />
      <CTA title="Talk directly with Carlo about your project" />
    </>
  );
}
